import React, { useState, useEffect, useCallback } from 'react';
import '../styles/nassar-theme.css';

const PHONE_NUMBER_MAX_LENGTH = 16;

const QuickAddModal = ({
    isOpen, 
    onClose, 
    onSave, 
    title, 
    fieldLabel, 
    fieldName = 'name', 
    placeholder,
    fields = null, // Array of field objects: [{ name: 'name', label: 'الاسم', placeholder: '...', required: true, type: 'text' }]
    editMode = false,
    editData = null
}) => {
    // Initialize form data based on fields or single field
    const getInitialFormData = useCallback(() => {
        if (editMode && editData) {
            // Pre-fill with edit data
            // If phone_num exists, split it into country code and number
            if (editData.phone_num) {
                const phoneNum = editData.phone_num;
                let countryCode = '+972';
                let phoneNumber = phoneNum;
                
                if (phoneNum.startsWith('+972')) {
                    countryCode = '+972';
                    phoneNumber = phoneNum.substring(4);
                } else if (phoneNum.startsWith('+970')) {
                    countryCode = '+970';
                    phoneNumber = phoneNum.substring(4);
                } else if (phoneNum.startsWith('+20')) {
                    countryCode = '+20';
                    phoneNumber = phoneNum.substring(3);
                } else if (phoneNum.startsWith('+962')) {
                    countryCode = '+962';
                    phoneNumber = phoneNum.substring(4);
                } else if (phoneNum.startsWith('+90')) {
                    countryCode = '+90';
                    phoneNumber = phoneNum.substring(3);
                } else if (phoneNum.startsWith('+86')) {
                    countryCode = '+86';
                    phoneNumber = phoneNum.substring(3);
                }
                
                return {
                    ...editData,
                    phone_country_code: countryCode,
                    phone_number: phoneNumber
                };
            }
            return editData;
        }
        if (fields && fields.length > 0) {
            const initialData = fields.reduce((acc, field) => {
                if (field.type === 'phone') {
                    acc['phone_country_code'] = '+972';
                    acc['phone_number'] = '';
                } else {
                    acc[field.name] = '';
                }
                return acc;
            }, {});
            return initialData;
        }
        return { [fieldName]: '' };
    }, [editMode, editData, fields, fieldName]);

    const [formData, setFormData] = useState(getInitialFormData());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Reset form data when modal opens/closes or fields change
    useEffect(() => {
        if (isOpen) {
            setFormData(getInitialFormData());
            setError('');
        }
    }, [isOpen, getInitialFormData]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (fields && fields.length > 0) {
            for (const field of fields) {
                if (field.type === 'phone') {
                    // Special validation for phone field
                    if (field.required && !formData['phone_number']?.trim()) {
                        setError(`${field.label} مطلوب`);
                        return;
                    }
                    // Validate phone number (max 16 digits)
                    const phoneNumber = formData['phone_number']?.trim();
                    if (phoneNumber && phoneNumber.length > PHONE_NUMBER_MAX_LENGTH) {
                        setError(`رقم الهاتف يجب ألا يتجاوز ${PHONE_NUMBER_MAX_LENGTH} خانة`);
                        return;
                    }
                    if (phoneNumber && !/^\d+$/.test(phoneNumber)) {
                        setError('رقم الهاتف يجب أن يحتوي على أرقام فقط');
                        return;
                    }
                    const fullPhone = (formData.phone_country_code || '') + (phoneNumber || '');
                    if (fullPhone.length > 20) {
                        setError('رقم الهاتف الكامل طويل جداً');
                        return;
                    }
                } else if (field.required && !formData[field.name]?.trim()) {
                    setError(`${field.label} مطلوب`);
                    return;
                }
            }
        } else {
            if (!formData[fieldName]?.trim()) {
                setError('هذا الحقل مطلوب');
                return;
            }
        }

        setIsLoading(true);
        setError('');

        try {
            // Prepare data to save
            const dataToSave = { ...formData };
            
            // If phone field exists, combine country code and number
            if (formData.phone_country_code && formData.phone_number) {
                dataToSave.phone_num = formData.phone_country_code + formData.phone_number;
                delete dataToSave.phone_country_code;
                delete dataToSave.phone_number;
            }
            
            await onSave(dataToSave);
            setFormData(getInitialFormData());
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'حدث خطأ أثناء الحفظ');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setFormData(getInitialFormData());
        setError('');
        onClose();
    };

    const handleFieldChange = (fieldName, value) => {
        // Special handling for phone number field - only allow digits and max 16 characters
        if (fieldName === 'phone_number') {
            const numericValue = value.replace(/\D/g, '');
            const truncatedValue = numericValue.slice(0, PHONE_NUMBER_MAX_LENGTH);
            setFormData(prev => ({ ...prev, [fieldName]: truncatedValue }));
        } else {
            setFormData(prev => ({ ...prev, [fieldName]: value }));
        }
        setError('');
    };

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button type="button" className="modal-close" onClick={handleClose}>
                        <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {fields && fields.length > 0 ? (
                            // Multiple fields mode
                            fields.map((field, index) => (
                                <div key={field.name} className="form-group" style={{ marginBottom: index < fields.length - 1 ? '1rem' : '0' }}>
                                    <label className="form-label">
                                        {field.label}
                                        {field.required && <span style={{ color: '#c33', marginRight: '4px' }}>*</span>}
                                    </label>
                                    {field.type === 'phone' ? (
                                        // Special phone input with country code dropdown
                                        <div style={{ display: 'flex', gap: '0.5rem', direction: 'ltr', alignItems: 'center' }}>
                                            <div style={{ position: 'relative', width: '130px' }}>
                                                <select
                                                    className="form-input"
                                                    value={formData.phone_country_code || '+972'}
                                                    onChange={(e) => handleFieldChange('phone_country_code', e.target.value)}
                                                    disabled={isLoading}
                                                    style={{ 
                                                        width: '100%',
                                                        fontWeight: '600',
                                                        fontSize: '0.95rem',
                                                        paddingLeft: '24px',
                                                        paddingRight: '12px',
                                                        cursor: 'pointer',
                                                        appearance: 'none',
                                                        WebkitAppearance: 'none',
                                                        MozAppearance: 'none',
                                                        background: 'white',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    <option value="+972">972+</option>
                                                    <option value="+970">970+</option>
                                                    <option value="+20">20+</option>
                                                    <option value="+962">962+</option>
                                                    <option value="+90">90+</option>
                                                    <option value="+86">86+</option>
                                                </select>
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '8px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    pointerEvents: 'none',
                                                    color: '#666'
                                                }}>
                                                    <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <input
                                                type="tel"
                                                className="form-input"
                                                value={formData.phone_number || ''}
                                                onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                                                placeholder={`أدخل حتى ${PHONE_NUMBER_MAX_LENGTH} خانة`}
                                                autoFocus={index === 0}
                                                disabled={isLoading}
                                                maxLength={PHONE_NUMBER_MAX_LENGTH}
                                                dir="ltr"
                                                style={{ 
                                                    flex: 1, 
                                                    textAlign: 'left',
                                                    paddingLeft: '12px',
                                                    letterSpacing: '1px'
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        // Regular input
                                        <input
                                            type={field.type || 'text'}
                                            className="form-input"
                                            value={formData[field.name] || ''}
                                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                            placeholder={field.placeholder}
                                            autoFocus={index === 0}
                                            disabled={isLoading}
                                        />
                                    )}
                                </div>
                            ))
                        ) : (
                            // Single field mode (backward compatibility)
                            <div className="form-group">
                                <label className="form-label">
                                    {fieldLabel}
                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData[fieldName] || ''}
                                    onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                    placeholder={placeholder}
                                    autoFocus
                                    disabled={isLoading}
                                />
                            </div>
                        )}

                        {error && (
                            <div className="error-message" style={{ marginTop: '1rem' }}>
                                <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button 
                            type="submit" 
                            className="btn btn-accent"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                                    <span>جاري الحفظ...</span>
                                </>
                            ) : (
                                <>
                                    <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>حفظ</span>
                                </>
                            )}
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-outline"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            <span>إلغاء</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default QuickAddModal;

